import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { T } from '../adminTheme';
import { PanelLeft, Search, Bell, Globe, Settings } from 'lucide-react';
import CommandPalette from './CommandPalette';
import API from '../utils/api';
import { onAdminRefresh } from '../utils/adminSocket';

// Icon-first topbar (matches the reference layout) — a page `title`, when
// given, still renders so the 15 not-yet-redesigned pages keep their
// heading; the Dashboard renders its own greeting instead and passes none.
const Navbar = ({ title }) => {
    const navigate = useNavigate();
    const { toggleSidebar } = useAdmin();
    const [showSearch, setShowSearch] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);

    useEffect(() => {
        const fetchUnread = () => {
            API.get('/admin/alerts/unread-count')
                .then(res => setUnreadNotifications(res.data.count || 0))
                .catch(() => { });
        };
        const handleLocalChange = event => {
            const count = Number(event.detail?.count);
            if (Number.isFinite(count)) setUnreadNotifications(Math.max(0, count));
            else fetchUnread();
        };

        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        const offRefresh = onAdminRefresh(fetchUnread);
        window.addEventListener('adminAlertsChanged', handleLocalChange);
        return () => {
            clearInterval(interval);
            offRefresh();
            window.removeEventListener('adminAlertsChanged', handleLocalChange);
        };
    }, []);

    // Ctrl/Cmd+K opens the command palette from anywhere, matching the
    // shortcut convention shown in the reference design.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setShowSearch(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    return (
        <div style={styles.navbar}>
            <div style={styles.left}>
                <button style={styles.iconBtn} onClick={toggleSidebar} title="Toggle menu">
                    <PanelLeft size={18} color={T.textSecondary} />
                </button>
                <button style={styles.iconBtn} title="Search (Ctrl+K)" onClick={() => setShowSearch(true)}>
                    <Search size={18} color={T.textSecondary} />
                </button>
                {title && (
                    <>
                        <span style={styles.divider} />
                        <h1 style={styles.title}>{title}</h1>
                    </>
                )}
            </div>
            <div style={styles.right}>
                <button
                    style={styles.iconBtn}
                    title={unreadNotifications ? `${unreadNotifications} unread notification${unreadNotifications === 1 ? '' : 's'}` : 'Notifications'}
                    aria-label={unreadNotifications ? `Notifications, ${unreadNotifications} unread` : 'Notifications'}
                    onClick={() => navigate('/notifications')}
                >
                    <Bell size={18} color={T.textSecondary} />
                    {unreadNotifications > 0 && (
                        <span style={styles.badge}>{unreadNotifications > 99 ? '99+' : unreadNotifications}</span>
                    )}
                </button>
                <button style={styles.iconBtn} title="View website"
                    onClick={() => window.open(process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000', '_blank')}>
                    <Globe size={18} color={T.textSecondary} />
                </button>
                <button style={styles.iconBtn} title="Settings" onClick={() => navigate('/profile-options')}>
                    <Settings size={18} color={T.textSecondary} />
                </button>
            </div>
            <CommandPalette open={showSearch} onClose={() => setShowSearch(false)} />
        </div>
    );
};

const styles = {
    navbar: {
        background: T.sidebarBg,
        padding: '0 24px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${T.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 50,
        gap: '12px',
    },
    left: { display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 },
    right: { display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 },
    iconBtn: {
        position: 'relative',
        width: '38px', height: '38px', borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0,
    },
    badge: {
        position: 'absolute', top: '1px', right: '-2px',
        minWidth: '17px', height: '17px', padding: '0 4px', boxSizing: 'border-box',
        display: 'grid', placeItems: 'center', borderRadius: '999px',
        background: T.danger, color: '#fff', border: `2px solid ${T.sidebarBg}`,
        fontSize: '8px', fontWeight: '800', lineHeight: 1,
        boxShadow: '0 3px 8px rgba(239, 68, 68, .28)',
    },
    divider: { width: '1px', height: '22px', background: T.border, margin: '0 8px', flexShrink: 0 },
    title: {
        fontSize: '16px',
        color: T.textPrimary,
        fontWeight: '700',
        letterSpacing: '-0.1px',
        margin: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

export default Navbar;
